import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Minimize2, PlayCircle, ChevronRight, Shield, Heart, Clock, ArrowRight } from "lucide-react";
import { useContent } from "../context/languageContext";
import Icon from "../resources/blue-logo-full.png";
import Button from "./Button";

interface VideoPageProps {
  videoUrl: string;
  onContinue: () => void;
  onSkip: () => void;
}

export default function VideoPage({ videoUrl, onContinue, onSkip }: VideoPageProps) {
  const [hasWatched, setHasWatched] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [duration, setDuration] = useState("0:00");
  const [currentTime, setCurrentTime] = useState("0:00");
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const content = useContent();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleVideoProgress = () => {
    if (!videoRef.current) return;
    const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setVideoProgress(progress);
    setCurrentTime(formatTime(videoRef.current.currentTime));

    if (progress >= 90 && !hasWatched) {
      setHasWatched(true);
    }
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(formatTime(videoRef.current.duration));
  };

  const handleVideoEnd = () => {
    setHasWatched(true);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content Container - Centered */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl">
          <div className="flex justify-center mb-5">
            <img src={Icon} alt="Icon" className="h-12 w-auto" />
          </div>
          {/* Compact Welcome Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-900 mb-2">{content("videoTitle")}</h1>
              <p className="text-gray-600 mb-3 text-sm">{content("videoDescription")}</p>

              {/* Quick Info Pills */}
              <div className="flex flex-wrap justify-center gap-2">
                <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  3-4 minutes
                </div>
                <div className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  100% Anoynmous
                </div>
                <div className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs">
                  <Heart className="w-3 h-3 mr-1" />
                  Personalized results
                </div>
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <PlayCircle className="w-4 h-4 mr-2 text-gray-600" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Learn More About Your Assessment</h3>
                    <p className="text-xs text-gray-500">Optional 3-minute introduction video</p>
                  </div>
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Optional</span>
              </div>
            </div>

            <div className="p-4">
              {/* Video container - Centered */}
              <div className="max-w-2xl mx-auto" ref={containerRef}>
                <div className="relative bg-black rounded-lg overflow-hidden shadow-lg">
                  <video
                    ref={videoRef}
                    className="w-full h-auto"
                    onTimeUpdate={handleVideoProgress}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleVideoEnd}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onClick={togglePlay}
                  >
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  {/* Video Overlay - Play Button */}
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer" onClick={togglePlay}>
                      <button className="bg-white/90 hover:bg-white rounded-full p-4 shadow-lg transform transition hover:scale-105">
                        <Play className="w-8 h-8 text-blue-600 ml-1" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Simplified Controls */}
                <div className="bg-gray-100 rounded-b-lg px-3 py-2 mt-2">
                  <div className="flex items-center space-x-2">
                    <button onClick={togglePlay} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label={isPlaying ? "Pause" : "Play"}>
                      {isPlaying ? <Pause className="w-4 h-4 text-gray-700" /> : <Play className="w-4 h-4 text-gray-700" />}
                    </button>

                    <div className="flex-1">
                      <div className="relative h-1 bg-gray-300 rounded-full overflow-hidden">
                        <div className="absolute h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${videoProgress}%` }} />
                      </div>
                    </div>

                    <span className="text-xs text-gray-600 min-w-[70px] text-right">
                      {currentTime} / {duration}
                    </span>

                    <button onClick={toggleMute} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label={isMuted ? "Unmute" : "Mute"}>
                      {isMuted ? <VolumeX className="w-4 h-4 text-gray-700" /> : <Volume2 className="w-4 h-4 text-gray-700" />}
                    </button>

                    <button onClick={toggleFullscreen} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label="Fullscreen">
                      {isFullscreen ? <Minimize2 className="w-4 h-4 text-gray-700" /> : <Maximize2 className="w-4 h-4 text-gray-700" />}
                    </button>
                  </div>
                </div>

                {/* Watched Confirmation */}
                {hasWatched && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-800 text-center">Thanks for watching! You can now begin your assessment.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Centered */}
          <div className="mt-6 text-center">
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              text={content("continue")}
              type="submit"
              size="lg"
              onClick={onSkip}
            />
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
